import clsx from 'clsx';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { Button, Divider, Typography } from '@/ui';

import PartnerCompanyInfo from './PartnerCompanyInfo';
import PartnerCompanyStats from './PartnerCompanyStats';
import PartnerMembers from './members/PartnerMembers';

import CollapsedCompanyHeader from '@/features/companyDashboard/common/CollapsedCompanyHeader';
import { useDownloadUsersCsv } from '@/features/companyDashboard/common/downloadUsersCsv';
import { useCompany } from '@/hooks/useCompany';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { isAdmin } from '@/types/user';

const PartnerCompanyPage = () => {
  const { companyId = '' } = useParams();
  const { companyData } = useCompany(companyId);
  const { user } = useCurrentUser();
  const { download } = useDownloadUsersCsv('partner', companyData?.name);
  const canEditFeatureFlags = isAdmin(user);
  const [isCompanyPanelCollapsed, setIsCompanyPanelCollapsed] = useState(false);

  const onDownloadCSV = async () => {
    try {
      await download();
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  if (!companyId) return null;
  if (!companyData) return null;

  // Content gutter for Members and the members table. The collapsed header sets
  // its own, intentionally narrower, gutter.
  const contentGutter = isCompanyPanelCollapsed ? 'px-20' : '';

  return (
    <div
      className={clsx('flex w-full flex-col lg:flex-row', {
        'gap-0': isCompanyPanelCollapsed,
        'gap-6': !isCompanyPanelCollapsed,
      })}
    >
      <PartnerCompanyInfo
        adminView={canEditFeatureFlags}
        company={companyData}
        isCollapsed={isCompanyPanelCollapsed}
        onCollapsedChange={setIsCompanyPanelCollapsed}
      />
      <div className="flex w-full min-w-0 flex-1 flex-col">
        {isCompanyPanelCollapsed ? (
          <CollapsedCompanyHeader
            company={companyData}
            stats={<PartnerCompanyStats isCompact />}
          />
        ) : (
          <PartnerCompanyStats />
        )}
        <div className={clsx('flex flex-col', contentGutter)}>
          <div className="flex items-center justify-between pt-10">
            <Typography variant="h4">Members</Typography>
            <Button variant="outlined" onClick={onDownloadCSV}>
              Download Users CSV
            </Button>
          </div>
          <Divider className="my-6" />
          <div className="w-full overflow-x-auto pb-4">
            <PartnerMembers />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerCompanyPage;
